import { FC, InputHTMLAttributes } from 'react';
import styled from 'styled-components';

import { COLORS } from '../../constants/colors';

type RadioProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>;

export const Radio: FC<RadioProps> = ({ children, className, ...inputProps }) => {
  return (
    <Label className={className}>
      <HiddenInput type="radio" {...inputProps} />
      <Circle />
      {children}
    </Label>
  );
};

// ===== styles =====

const Circle = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1.5px solid ${COLORS.systemDark100};
  background: transparent;
  transition:
    border-color var(--ani-speed),
    background-color var(--ani-speed);

  &::after {
    content: '';
    position: absolute;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${COLORS.systemLight100};
    opacity: 0;
    transition: opacity var(--ani-speed);
  }
`;

const HiddenInput = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  margin: 0;
  pointer-events: none;
`;

const Label = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;

  &:has(${HiddenInput}:disabled) {
    cursor: not-allowed;
  }

  // checked
  ${HiddenInput}:checked ~ ${Circle} {
    border-color: ${COLORS.systemSky200};
    background: ${COLORS.systemSky200};

    &::after {
      opacity: 1;
    }
  }

  // hover unchecked
  &:not(:has(${HiddenInput}:disabled)):hover ${HiddenInput}:not(:checked) ~ ${Circle} {
    border-color: ${COLORS.systemSky200};
  }

  // hover checked
  &:not(:has(${HiddenInput}:disabled)):hover ${HiddenInput}:checked ~ ${Circle} {
    border-color: ${COLORS.systemSky300};
    background: ${COLORS.systemSky300};
  }

  // disabled unchecked
  ${HiddenInput}:disabled ~ ${Circle} {
    border-color: ${COLORS.systemLight300};
    background: ${COLORS.systemLight300};
  }

  // disabled checked
  ${HiddenInput}:disabled:checked ~ ${Circle} {
    border-color: ${COLORS.systemDark100};
    background: ${COLORS.systemDark100};
  }
`;
